import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Typography, Button, TextField, Card, CardContent,
  Chip, Divider, Alert, CircularProgress, Avatar
} from '@mui/material';
import {
  Search, Email, Cancel, CheckCircle, CalendarMonth,
  Person, People, AttachMoney, ArrowBack, ConfirmationNumber,
  Star, Home
} from '@mui/icons-material';
import api from '../services/api';

const MyBookings = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setError(null);
    setSearched(true);

    try {
      const res = await api.get(`/bookings/guest/email/${email}`);
      setBookings(res.data.data || []);
    } catch (err) {
      setError('Failed to find bookings');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (bookingId, token) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;

    setCancelling(bookingId);
    try {
      await api.put(`/bookings/guest/${token}/cancel`);
      const res = await api.get(`/bookings/guest/email/${email}`);
      setBookings(res.data.data || []);
    } catch (err) {
      alert('Failed to cancel booking');
    } finally {
      setCancelling(null);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      confirmed: 'success',
      checked_in: 'info',
      checked_out: 'default',
      cancelled: 'error'
    };
    return colors[status] || 'default';
  };

  return (
    <Box sx={{
      background: 'linear-gradient(180deg, #f5f0e8 0%, #faf8f3 50%, #f5f0e8 100%)',
      minHeight: '100vh',
      py: { xs: 4, md: 6 },
      px: { xs: 2, md: 4 }
    }}>
      <Box sx={{ maxWidth: 1000, mx: 'auto' }}>

        {/* Back */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{
            mb: 3,
            color: '#1a237e',
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': { bgcolor: 'rgba(26,35,126,0.08)' }
          }}
        >
          Back to Home
        </Button>

        {/* Header */}
        <Box textAlign="center" mb={5}>
          <Typography
            variant="overline"
            sx={{ color: '#ff6f00', fontWeight: 700, letterSpacing: 3, fontSize: '0.9rem' }}
          >
            MANAGE YOUR STAY
          </Typography>
          <Typography variant="h3" fontWeight={800} color="#1a237e" sx={{ mt: 1 }}>
            My Bookings
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Enter your email to find and manage your reservations
          </Typography>
        </Box>

        {/* Search */}
        <Card sx={{
          borderRadius: '24px',
          boxShadow: '0 15px 50px rgba(26,35,126,0.1)',
          bgcolor: '#fdfbf6',
          mb: 4
        }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <form onSubmit={handleSearch}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="The email you used to book"
                    required
                    InputProps={{
                      startAdornment: <Email sx={{ mr: 1, color: '#999' }} />,
                      sx: {
                        borderRadius: '12px',
                        bgcolor: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(26,35,126,0.15)'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1a237e' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ff6f00' }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    startIcon={<Search />}
                    sx={{
                      py: 1.7,
                      borderRadius: '12px',
                      bgcolor: '#1a237e',
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '1rem',
                      '&:hover': { bgcolor: '#0d47a1' }
                    }}
                  >
                    {loading ? 'Searching...' : 'Find My Bookings'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>

        {/* Loading */}
        {loading && (
          <Box textAlign="center" py={6}>
            <CircularProgress sx={{ color: '#ff6f00' }} />
          </Box>
        )}

        {/* Results */}
        {!loading && searched && (
          <>
            {error && (
              <Alert severity="error" sx={{ borderRadius: '16px', mb: 3 }}>
                {error}
              </Alert>
            )}

            {bookings.length === 0 && !error && (
              <Card sx={{
                borderRadius: '24px',
                bgcolor: '#fdfbf6',
                textAlign: 'center',
                p: 5
              }}>
                <Box sx={{
                  width: 80, height: 80, borderRadius: '50%',
                  bgcolor: '#f5f0e8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  mx: 'auto', mb: 2
                }}>
                  <ConfirmationNumber sx={{ fontSize: 40, color: '#1a237e' }} />
                </Box>
                <Typography variant="h5" fontWeight={700} color="#1a237e" gutterBottom>
                  No Bookings Found
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  We couldn't find any bookings for <strong>{email}</strong>
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/')}
                  sx={{
                    bgcolor: '#ff6f00',
                    borderRadius: '50px',
                    px: 4,
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#e65100' }
                  }}
                >
                  Make a Booking
                </Button>
              </Card>
            )}

            {bookings.length > 0 && (
              <>
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Found <strong>{bookings.length}</strong> booking{bookings.length > 1 ? 's' : ''} for {email}
                  </Typography>
                </Box>

                <Grid container spacing={3}>
                  {bookings.map((booking) => (
                    <Grid item xs={12} key={booking._id}>
                      <Card sx={{
                        borderRadius: '24px',
                        boxShadow: '0 10px 40px rgba(26,35,126,0.08)',
                        bgcolor: '#fdfbf6',
                        overflow: 'hidden',
                        transition: 'all 0.3s',
                        '&:hover': {
                          boxShadow: '0 20px 60px rgba(26,35,126,0.15)',
                          transform: 'translateY(-3px)'
                        }
                      }}>
                        <Box sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', md: 'row' }
                        }}>
                          {/* Image */}
                          <Box
                            component="img"
                            src={booking.room?.images?.[0] || '/images/1.jpg'}
                            alt={booking.room?.name}
                            sx={{
                              width: { xs: '100%', md: 220 },
                              height: { xs: 180, md: 'auto' },
                              objectFit: 'cover'
                            }}
                          />

                          {/* Content */}
                          <CardContent sx={{ flex: 1, p: 3 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="start" flexWrap="wrap" gap={2} mb={2}>
                              <Box>
                                <Typography variant="overline" color="#ff6f00" fontWeight={700}>
                                  BOOKING #{booking._id?.slice(-8).toUpperCase()}
                                </Typography>
                                <Typography variant="h6" fontWeight={700} color="#1a237e">
                                  {booking.room?.name || 'Entire House'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {booking.guestName}
                                </Typography>
                              </Box>
                              <Chip
                                label={booking.status?.toUpperCase()}
                                color={getStatusColor(booking.status)}
                                sx={{ fontWeight: 700 }}
                              />
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Grid container spacing={2}>
                              <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Check-in
                                </Typography>
                                <Typography variant="body2" fontWeight={600} color="#1a237e">
                                  {new Date(booking.checkIn).toLocaleDateString('en-US', {
                                    month: 'short', day: 'numeric', year: 'numeric'
                                  })}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Check-out
                                </Typography>
                                <Typography variant="body2" fontWeight={600} color="#1a237e">
                                  {new Date(booking.checkOut).toLocaleDateString('en-US', {
                                    month: 'short', day: 'numeric', year: 'numeric'
                                  })}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Guests
                                </Typography>
                                <Typography variant="body2" fontWeight={600} color="#1a237e">
                                  {booking.guests}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Total
                                </Typography>
                                <Typography variant="body2" fontWeight={700} color="#ff6f00">
                                  ${booking.totalPrice}
                                </Typography>
                              </Grid>
                            </Grid>

                            {/* Actions */}
                            {['pending', 'confirmed'].includes(booking.status) && (
                              <Box mt={2} display="flex" gap={1}>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  startIcon={<Cancel />}
                                  onClick={() => handleCancel(booking._id, booking.bookingToken)}
                                  disabled={cancelling === booking._id}
                                  sx={{
                                    borderRadius: '50px',
                                    textTransform: 'none',
                                    fontWeight: 600
                                  }}
                                >
                                  {cancelling === booking._id ? 'Cancelling...' : 'Cancel Booking'}
                                </Button>
                              </Box>
                            )}

                            {booking.status === 'cancelled' && (
                              <Alert severity="error" sx={{ mt: 2, borderRadius: '12px' }}>
                                This booking has been cancelled
                              </Alert>
                            )}

                            {booking.status === 'checked_out' && (
                              <Alert severity="success" sx={{ mt: 2, borderRadius: '12px' }}>
                                ✓ Thank you for staying with us!
                              </Alert>
                            )}
                          </CardContent>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default MyBookings;